import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconPlus,
  IconEdit,
  IconFileText,
  IconMapPin,
  IconMapPinOff,
  IconTrash,
  IconPaperclip,
  IconPhoto,
} from '@tabler/icons-react';
import type { Icon } from '@tabler/icons-react';

import type {
  OperationHistoryConfig,
  OperationHistoryEvent,
  OperationEventColor,
} from '@/components/common/OperationHistory';
import { NoteHistoryEventType } from '@/types/notes';
import type { NoteHistoryEvent } from '@/types/notes';
import { lowerFirst } from '@/utils/helpers';

const NOTE_EVENT_ICONS: Record<NoteHistoryEventType, Icon> = {
  [NoteHistoryEventType.NoteCreated]: IconPlus,
  [NoteHistoryEventType.NoteUpdated]: IconEdit,
  [NoteHistoryEventType.NoteDeleted]: IconTrash,
  [NoteHistoryEventType.TitleChanged]: IconEdit,
  [NoteHistoryEventType.ContentChanged]: IconFileText,
  [NoteHistoryEventType.LocationAdded]: IconMapPin,
  [NoteHistoryEventType.LocationChanged]: IconMapPin,
  [NoteHistoryEventType.LocationRemoved]: IconMapPinOff,
  [NoteHistoryEventType.AttachmentAdded]: IconPaperclip,
  [NoteHistoryEventType.AttachmentDeleted]: IconPaperclip,
  [NoteHistoryEventType.ImageUploaded]: IconPhoto,
};

const NOTE_EVENT_COLORS: Record<NoteHistoryEventType, OperationEventColor> = {
  [NoteHistoryEventType.NoteCreated]: 'green',
  [NoteHistoryEventType.NoteUpdated]: 'indigo',
  [NoteHistoryEventType.NoteDeleted]: 'red',
  [NoteHistoryEventType.TitleChanged]: 'indigo',
  [NoteHistoryEventType.ContentChanged]: 'indigo',
  [NoteHistoryEventType.LocationAdded]: 'purple',
  [NoteHistoryEventType.LocationChanged]: 'purple',
  [NoteHistoryEventType.LocationRemoved]: 'red',
  [NoteHistoryEventType.AttachmentAdded]: 'green',
  [NoteHistoryEventType.AttachmentDeleted]: 'red',
  [NoteHistoryEventType.ImageUploaded]: 'green',
};

export function mapNoteEvent(event: NoteHistoryEvent): OperationHistoryEvent {
  return {
    id: event.id,
    eventType: event.eventType,
    description: event.description,
    authorName: event.authorName,
    authorId: event.authorId,
    authorAvatarUrl: event.authorAvatarUrl,
    createdAt: event.createdAt,
    metadata: event.metadata as Record<string, unknown> | null,
  };
}

function isKnownNoteEventType(type: string): type is NoteHistoryEventType {
  return type in NOTE_EVENT_ICONS;
}

export function useNoteHistoryConfig(): OperationHistoryConfig {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      getEventIcon: (event) =>
        isKnownNoteEventType(event.eventType)
          ? NOTE_EVENT_ICONS[event.eventType]
          : IconEdit,

      getEventColor: (event) =>
        isKnownNoteEventType(event.eventType)
          ? NOTE_EVENT_COLORS[event.eventType]
          : 'gray',

      getEventSummary: (event) =>
        t(`notes.history.events.${lowerFirst(event.eventType)}`, {
          defaultValue: event.description,
        }),

      getEventDiff: (event) => {
        const meta = event.metadata as {
          oldValue?: string;
          newValue?: string;
        } | null;
        if (!meta) return null;

        // Content changes are too long to show as diff
        if (event.eventType === NoteHistoryEventType.ContentChanged) {
          return null;
        }

        if (meta.oldValue && meta.newValue) {
          return { from: meta.oldValue, to: meta.newValue };
        }
        if (meta.newValue) return { to: meta.newValue };
        if (meta.oldValue) return { from: meta.oldValue };

        return null;
      },

      getEventTypeLabel: (eventType) =>
        t(`notes.history.eventTypeLabels.${lowerFirst(eventType)}`, {
          defaultValue: eventType,
        }),

      allEventTypes: Object.values(NoteHistoryEventType) as readonly string[],
    }),
    [t]
  );
}
